import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineInformationCircle } from "react-icons/hi";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

import {
  getAssignmentStampPaperFromVendorPreview,
  saveAssignmentStampPaperFromVendor,
  type AssignmentStampPaperFromVendorPreview,
  type SaveAssignmentStampPaperFromVendorPayload,
} from "../../api/assignment";

export const ASSIGNMENT_STAMP_PAPER_FROM_VENDOR_FORM_ID =
  "assignment-stamp-paper-from-vendor-form";

// Rates per backend spec (assignment-stamp-paper.helpers.ts → calculateVendorAmount):
// PoA stamps Rs.610, Assignment Deed Rs.510, Other Documents Rs.330.
const RATE_POA = 610;
const RATE_ASSIGNMENT_DEED = 510;
const RATE_OTHER_DOCS = 330;

const LABEL_STYLE = "text-xs font-normal leading-5 flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;
const INPUT_BASE =
  "box-border flex flex-row items-center justify-between gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222]";
const PLACEHOLDER_CLASS =
  "text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]";
const TEXT_CLASS =
  "text-[12px] md:text-xs font-normal leading-5 text-[#222222]";
const TEXT_MUTED = "text-xs font-normal leading-5 text-[#6D6D6D]";

function ReadOnlyCountField({
  label,
  value,
}: {
  label: string;
  value: number;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col gap-1 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label
        className={LABEL_STYLE}
        style={{ ...LABEL_FEATURES, color: "#6D6D6D" }}
      >
        {label}
      </label>
      <div
        className="box-border flex flex-row items-center justify-between gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-[#EEEEEE] border border-[#E0E0E0] rounded-[6px] cursor-not-allowed text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]"
        aria-disabled="true"
        aria-readonly="true"
        style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
      >
        <span className="min-w-0 flex-1 truncate">{String(value)}</span>
      </div>
    </div>
  );
}

type AssignmentStampPaperFromVendorViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function AssignmentStampPaperFromVendorView({
  leadId,
  onSuccess,
}: AssignmentStampPaperFromVendorViewProps) {
  // Stamp counts (read-only — entered at Step 1)
  const [applicantPoaStamps, setApplicantPoaStamps] = useState(0);
  const [assigneePoaStamps, setAssigneePoaStamps] = useState(0);
  const [assignmentDeedStamps, setAssignmentDeedStamps] = useState(0);
  const [otherDocumentsStamps, setOtherDocumentsStamps] = useState(0);

  // 31r) Amount Payable — editable override; null means use calculated.
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  const [amount, setAmount] = useState(0);
  const [hasOverride, setHasOverride] = useState(false);

  // 31s) Paid to Vendor
  const [paidToVendor, setPaidToVendor] = useState<boolean | null>(null);

  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    paidToVendor?: boolean;
    amount?: boolean;
  }>({});

  const [calcTooltipOpen, setCalcTooltipOpen] = useState(false);
  const calcTooltipRef = useRef<HTMLDivElement>(null);

  const autoSaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyPreview = (data: AssignmentStampPaperFromVendorPreview) => {
    setApplicantPoaStamps(data.applicantPoaStamps ?? 0);
    setAssigneePoaStamps(data.assigneePoaStamps ?? 0);
    setAssignmentDeedStamps(data.assignmentDeedStamps ?? 0);
    setOtherDocumentsStamps(data.otherDocumentsStamps ?? 0);
    setCalculatedAmount(data.calculatedVendorAmount ?? 0);
    setHasOverride(data.vendorAmountPayable !== null);
    setAmount(
      data.vendorAmountPayable !== null
        ? data.vendorAmountPayable
        : (data.calculatedVendorAmount ?? 0)
    );
    setPaidToVendor(data.vendorPaid ?? false);
  };

  useEffect(() => {
    if (!calcTooltipOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        calcTooltipRef.current &&
        !calcTooltipRef.current.contains(e.target as Node)
      ) {
        setCalcTooltipOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [calcTooltipOpen]);

  // Initial load: preview + workflow progress.
  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAssignmentStampPaperFromVendorPreview(leadId)
      .then((preview) => {
        if (cancelled) return;
        applyPreview(preview);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load stamp paper payment details"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const buildPayload = (): SaveAssignmentStampPaperFromVendorPayload => ({
    // null clears the override; sending the override value when it differs from calc keeps it.
    vendorAmountPayable: hasOverride ? amount : null,
    vendorPaid: paidToVendor ?? undefined,
  });

  // Auto-save: amount override changes + paid flag — debounced.
  useEffect(() => {
    if (!leadId || loading) return;
    if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
    autoSaveDebounceRef.current = setTimeout(() => {
      saveAssignmentStampPaperFromVendor(leadId, buildPayload())
        .then((data) => {
          // Refresh calculated amount in case Step 1 counts changed between visits.
          setCalculatedAmount(data.calculatedVendorAmount ?? 0);
        })
        .catch(() => {
          // Silent — the explicit submit surfaces real failures.
        });
    }, 600);
    return () => {
      if (autoSaveDebounceRef.current)
        clearTimeout(autoSaveDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, amount, hasOverride, paidToVendor]);

  const handlePaidToVendorChange = (value: string): void => {
    if (value === "__none__") {
      setPaidToVendor(null);
      return;
    }
    setPaidToVendor(value === "yes");
  };

  const paidToVendorSelectValue =
    paidToVendor === true ? "yes" : paidToVendor === false ? "no" : "__none__";

  const handleAmountChange = (raw: string) => {
    if (raw === "") {
      setAmount(0);
      setHasOverride(true);
      return;
    }
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0) {
      setAmount(n);
      setHasOverride(n !== calculatedAmount);
    }
  };

  const handleResetOverride = () => {
    setHasOverride(false);
    setAmount(calculatedAmount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    const errs: { paidToVendor?: boolean; amount?: boolean } = {};
    if (paidToVendor === null) errs.paidToVendor = true;
    if (amount < 0) errs.amount = true;
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      const data = await saveAssignmentStampPaperFromVendor(
        leadId,
        buildPayload()
      );
      applyPreview(data);
      toast.success("Stamp paper payment saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Loading stamp paper payment details...
        </p>
      </div>
    );
  }

  const poaTotalStamps = applicantPoaStamps + assigneePoaStamps;
  const poaSubtotal = poaTotalStamps * RATE_POA;
  const deedSubtotal = assignmentDeedStamps * RATE_ASSIGNMENT_DEED;
  const otherSubtotal = otherDocumentsStamps * RATE_OTHER_DOCS;

  return (
    <form
      id={ASSIGNMENT_STAMP_PAPER_FROM_VENDOR_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <h3 className="text-sm font-medium text-[#222222]">
        Stamp Paper from Vendor for Assignment
      </h3>

      {/* Row 1: Amount Payable to Stamp Vendor | Paid to Vendor */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        {/* Amount Payable to Stamp Vendor (editable + tooltip) */}
        <div className="flex flex-1 min-w-0 flex-col gap-1 w-full">
          <div className="flex items-center gap-1">
            <label
              className={LABEL_STYLE}
              style={{ ...LABEL_FEATURES, color: "#222222" }}
            >
              Amount Payable to Stamp Vendor
            </label>
            <div className="relative" ref={calcTooltipRef}>
              <button
                type="button"
                onClick={() => setCalcTooltipOpen((o) => !o)}
                onMouseEnter={() => setCalcTooltipOpen(true)}
                className="flex items-center justify-center text-[#6D6D6D] hover:text-[#222222]"
                aria-label="Show calculation"
              >
                <HiOutlineInformationCircle
                  className="w-4 h-4"
                  strokeWidth={1.5}
                />
              </button>
              {calcTooltipOpen && (
                <div
                  className="absolute left-0 top-full mt-1 z-30 w-72 rounded-[8px] border border-[#EEEEEE] bg-white shadow-lg p-3 text-[12px] leading-5 text-[#222222]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <p className="text-xs font-semibold text-[#101111] mb-2">
                    Calculation Breakdown
                  </p>
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between gap-3">
                      <span className="text-[#6D6D6D]">
                        PoA stamps ({poaTotalStamps} × Rs.{" "}
                        {RATE_POA.toLocaleString("en-IN")})
                      </span>
                      <span>Rs. {poaSubtotal.toLocaleString("en-IN")}/-</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-[#6D6D6D]">
                        Assignment Deed ({assignmentDeedStamps} × Rs.{" "}
                        {RATE_ASSIGNMENT_DEED.toLocaleString("en-IN")})
                      </span>
                      <span>Rs. {deedSubtotal.toLocaleString("en-IN")}/-</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-[#6D6D6D]">
                        Other Documents ({otherDocumentsStamps} × Rs.{" "}
                        {RATE_OTHER_DOCS.toLocaleString("en-IN")})
                      </span>
                      <span>Rs. {otherSubtotal.toLocaleString("en-IN")}/-</span>
                    </div>
                    <div className="border-t border-[#222] pt-1 mt-1 flex justify-between font-semibold text-[#101111]">
                      <span>Auto-calc Total</span>
                      <span>
                        Rs. {calculatedAmount.toLocaleString("en-IN")}/-
                      </span>
                    </div>
                    <p className="mt-2 text-[11px] leading-4 text-[#6D6D6D]">
                      This field is editable — you can override the
                      auto-calculated amount if the vendor's invoice differs.
                    </p>
                    {hasOverride && amount !== calculatedAmount && (
                      <button
                        type="button"
                        onClick={handleResetOverride}
                        className="mt-1 self-start text-[11px] leading-4 text-[#222] underline"
                      >
                        Reset to auto-calc (Rs.{" "}
                        {calculatedAmount.toLocaleString("en-IN")}/-)
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div
            className={`${INPUT_BASE}${
              fieldErrors.amount ? " !border-red-500" : ""
            }`}
          >
            <span className={`shrink-0 ${TEXT_MUTED}`}>Rs.</span>
            <input
              type="number"
              min={0}
              value={amount}
              onChange={(e) => {
                handleAmountChange(e.target.value);
                setFieldErrors((p) => ({ ...p, amount: false }));
              }}
              placeholder="Text Here"
              className="flex-1 min-w-0 bg-transparent border-0 text-xs font-normal leading-5 text-[#222222] focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            />
          </div>
        </div>

        {/* Paid to Vendor */}
        <div className="flex flex-1 min-w-0 flex-col gap-1 w-full">
          <label
            className={LABEL_STYLE}
            style={{ ...LABEL_FEATURES, color: "#222222" }}
          >
            Paid to Vendor
          </label>
          <Select
            value={paidToVendorSelectValue}
            onValueChange={(v) => {
              handlePaidToVendorChange(v);
              if (v !== "__none__") setFieldErrors((p) => ({ ...p, paidToVendor: false }));
            }}
          >
            <SelectTrigger
              className={`h-9 min-h-[44px] md:min-h-[36px] w-full border rounded-[6px] px-2.5 py-2 text-[12px] md:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D] ${
                fieldErrors.paidToVendor ? "border-red-500" : "border-[#eee]"
              }`}
            >
              <SelectValue placeholder="Text Here" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Select</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
          {paidToVendor === false ? (
            <p
              className="text-xs font-medium leading-5 text-[#222222] mt-0.5"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
              role="status"
            >
              Status: Pending Payment
            </p>
          ) : paidToVendor === true ? (
            <p
              className="text-xs font-medium leading-5 text-[#222222] mt-0.5"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
              role="status"
            >
              Status: Paid
            </p>
          ) : null}
        </div>
      </div>

      {/* Row 2: Number of Stamp Papers for PoA | Assignee PoA — read-only counts from Step 1 */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <ReadOnlyCountField
          label="Number of Stamp Papers for PoA"
          value={applicantPoaStamps}
        />
        <ReadOnlyCountField
          label="Number of Stamp Papers for Assignee PoA"
          value={assigneePoaStamps}
        />
      </div>

      {/* Row 3: Assignment Deed | Other Documents — read-only counts from Step 1 */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <ReadOnlyCountField
          label="Number of Stamp Papers for Assignment Deed"
          value={assignmentDeedStamps}
        />
        <ReadOnlyCountField
          label="Number of Stamp Papers for Other Documents"
          value={otherDocumentsStamps}
        />
      </div>

    </form>
  );
}
